import { z } from "zod";

// ---------------------------------------------------------------------------
// Daily tasks ("Dagens uppgifter")
//
// The store manager sends an ordered task list to a department head for a
// day; the head starts, finishes and rates each task; the manager follows up.
// Stored at:
//   Businesses/{businessId}/dailyTasks/{taskId}
// ---------------------------------------------------------------------------

export const DailyTaskStatus = z.enum(["new", "started", "done"]);
export type DailyTaskStatus = z.infer<typeof DailyTaskStatus>;

export const DailyTaskRating = z.enum(["bra", "okej", "tungt"]);
export type DailyTaskRating = z.infer<typeof DailyTaskRating>;

export const DailyTaskDeadline = z.enum(["today", "tomorrow", "week"]);
export type DailyTaskDeadline = z.infer<typeof DailyTaskDeadline>;

export interface DailyTaskRecord {
  id: string;
  businessId: string;
  /** The department head (roster member id) the task was sent to. */
  headMemberId: string;
  salesTeamId: string | null;
  /** YYYY-MM-DD the task belongs to. */
  date: string;
  order: number;
  title: string;
  /** Optional clock time (HH:MM) the manager attached when sending, e.g. "a WhatsApp list row/template shows the first task's time" (GRA-289). Null when not set. */
  time: string | null;
  requiresPhoto: boolean;
  deadline: DailyTaskDeadline;
  status: DailyTaskStatus;
  startedAt: string | null;
  doneAt: string | null;
  minutes: number | null;
  rating: DailyTaskRating | null;
  comment: string | null;
  photoPath: string | null;
  /** "anser" when Anser suggested it, "adhoc" for a GRA-292 one-off task to any staff member, otherwise null. */
  source: string | null;
  sentByUid: string | null;
  /** A "Fråga" (GRA-292) the assignee asked about this specific task, distinct from `comment` (set after finishing). */
  question: string | null;
  questionAskedAt: string | null;
  /** The manager's reply to `question`, sent back to the assignee over WhatsApp. */
  managerReply: string | null;
  managerReplyAt: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

/**
 * Deterministic answer to "is this task overdue right now" (GRA-292):
 * not stored on the record, since "now" changes - recomputed wherever it's
 * needed (the manager follow-up view, GRA-290's alert).
 */
export function isOverdueDailyTask(
  task: Pick<DailyTaskRecord, "status" | "date" | "time">,
  now: Date,
): boolean {
  if (task.status === "done") return false;
  const todayKey = now.toISOString().slice(0, 10);
  if (task.date < todayKey) return true;
  if (task.date > todayKey) return false;
  if (!task.time) return false;
  const nowClock = now.toISOString().slice(11, 16);
  return task.time < nowClock;
}

export const ListDailyTasksInput = z.object({
  businessId: z.string().min(1),
  /** YYYY-MM-DD, inclusive. */
  from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  to: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  headMemberId: z.string().min(1).optional(),
});
export type ListDailyTasksInput = z.infer<typeof ListDailyTasksInput>;

/** GRA-292: open tasks already past their deadline, across the business - feeds the manager's overdue view. */
export const ListOverdueDailyTasksInput = z.object({
  businessId: z.string().min(1),
});
export type ListOverdueDailyTasksInput = z.infer<typeof ListOverdueDailyTasksInput>;

export const SendDailyTasksInput = z.object({
  businessId: z.string().min(1),
  headMemberIds: z.array(z.string().min(1)).min(1),
  /** YYYY-MM-DD dates the same list applies to. */
  dates: z.array(z.string().regex(/^\d{4}-\d{2}-\d{2}$/)).min(1),
  tasks: z
    .array(
      z.object({
        title: z.string().min(1).max(160),
        time: z
          .string()
          .regex(/^\d{2}:\d{2}$/)
          .nullable()
          .optional(),
        requiresPhoto: z.boolean().default(false),
        deadline: DailyTaskDeadline.default("today"),
        source: z.string().min(1).nullable().optional(),
      }),
    )
    .min(1),
});
export type SendDailyTasksInput = z.infer<typeof SendDailyTasksInput>;

/**
 * One-off ad-hoc task to any staff member (GRA-292) - simpler shape than
 * `SendDailyTasksInput`'s ordered multi-task/multi-date list: one task, one
 * target date, one or more recipients. The router forces `source: "adhoc"`
 * when building the underlying `sendDailyTasks` call; a client can't set it.
 */
export const SendAdHocTaskInput = z.object({
  businessId: z.string().min(1),
  headMemberIds: z.array(z.string().min(1)).min(1),
  /** YYYY-MM-DD the task is due. */
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  title: z.string().min(1).max(160),
  time: z
    .string()
    .regex(/^\d{2}:\d{2}$/)
    .nullable()
    .optional(),
  requiresPhoto: z.boolean().default(false),
});
export type SendAdHocTaskInput = z.infer<typeof SendAdHocTaskInput>;

export const SetDailyTaskStatusInput = z.object({
  businessId: z.string().min(1),
  taskId: z.string().min(1),
  status: DailyTaskStatus,
});
export type SetDailyTaskStatusInput = z.infer<typeof SetDailyTaskStatusInput>;

export const RateDailyTaskInput = z.object({
  businessId: z.string().min(1),
  taskId: z.string().min(1),
  rating: DailyTaskRating,
  minutes: z.number().int().min(0).max(600).nullable().optional(),
  comment: z.string().max(500).nullable().optional(),
});
export type RateDailyTaskInput = z.infer<typeof RateDailyTaskInput>;

/**
 * Attaches a photo to a `requiresPhoto` task (GRA-289 - the WhatsApp finish
 * flow is the first writer; there was no path to set `photoPath` before).
 * `photoPath` must fall under `dailyTaskPhotoObjectsPrefix` for this exact
 * task (checked in the router, see `routines.setCompletion`'s
 * `assertPhotoPathForCompletion` for the equivalent check on that domain).
 */
export const AttachDailyTaskPhotoInput = z.object({
  businessId: z.string().min(1),
  taskId: z.string().min(1),
  photoPath: z.string().min(1),
});
export type AttachDailyTaskPhotoInput = z.infer<typeof AttachDailyTaskPhotoInput>;

/** "Fråga" (GRA-292): the assignee asks a question about a specific ad-hoc task. */
export const AskDailyTaskQuestionInput = z.object({
  businessId: z.string().min(1),
  taskId: z.string().min(1),
  question: z.string().min(1).max(500),
});
export type AskDailyTaskQuestionInput = z.infer<typeof AskDailyTaskQuestionInput>;

/** The manager's reply to a `question`, sent back over WhatsApp (GRA-292). */
export const AnswerDailyTaskQuestionInput = z.object({
  businessId: z.string().min(1),
  taskId: z.string().min(1),
  reply: z.string().min(1).max(500),
});
export type AnswerDailyTaskQuestionInput = z.infer<typeof AnswerDailyTaskQuestionInput>;
